import { FlameIcon, HexagonIcon, SparkIcon } from './icons';
import { useStrings } from '../state/AppContext';

export interface StatStripProps {
  streak: number;
  wins: number;
  xp: number;
}

// Visible at every width down to 360px — I5's layout unification freed
// enough vertical room to keep the mockup's captions on mobile too. Below
// 360px there isn't room to spare, so the caption drops; the wrapping
// element's `aria-label` already carries the full "N day streak" /
// "N wins" / "N total XP" text for a screen reader, so hiding the caption
// line there costs no accessible information, only pixels.
const CAPTION_CLASS = 'block text-[10px] tracking-[0.16em] text-dim max-[359px]:hidden';

export function StatStrip({ streak, wins, xp }: StatStripProps) {
  const t = useStrings();
  return (
    <div
      className="grid grid-cols-3 items-center gap-1 px-4 py-0.5 sm:gap-2 sm:py-3"
      role="group"
      aria-label={t.stats.yourStats}
    >
      <div className="flex flex-col items-center gap-0.5" aria-label={t.stats.streakLabel(streak)}>
        <div className="flex items-center gap-1.5">
          <FlameIcon className="h-4 w-4 text-flame sm:h-5 sm:w-5" />
          <span className="text-lg font-bold sm:text-xl">{streak}</span>
        </div>
        <span className={CAPTION_CLASS}>{t.stats.dayStreak}</span>
      </div>

      <div
        className="relative grid place-items-center border-l border-hairline"
        aria-label={t.stats.winsLabel(wins)}
      >
        <span className="relative grid h-7 w-7 place-items-center sm:h-14 sm:w-14">
          <HexagonIcon className="absolute h-7 w-7 text-present sm:h-14 sm:w-14" />
          <span className="relative text-sm font-bold sm:text-lg">{wins}</span>
        </span>
        <span className={CAPTION_CLASS}>{t.stats.wins}</span>
      </div>

      <div
        className="flex flex-col items-center gap-0.5 border-l border-hairline"
        aria-label={t.stats.xpLabel(xp)}
      >
        <div className="flex items-center gap-1.5">
          <SparkIcon className="h-4 w-4 text-accent sm:h-5 sm:w-5" />
          <span className="text-lg font-bold sm:text-xl">{xp}</span>
        </div>
        <span className={CAPTION_CLASS}>{t.stats.totalXp}</span>
      </div>
    </div>
  );
}
